/**
 * MarkdownRenderer - json-render component
 * 
 * Renders markdown content with proper styling.
 * Used for rich text display in agent messages.
 */

import ReactMarkdown from 'react-markdown';
import { cn } from '@pagespeedify/ui';

export interface MarkdownRendererProps {
    content: string;
    className?: string;
}

function normalizeDisplayMarkdown(content: string): string {
    if (!content) return '';
    let normalized = content
        .replace(/â€”/g, '-')
        .replace(/â€“/g, '-')
        .replace(/â€˜/g, "'")
        .replace(/â€™/g, "'")
        .replace(/â€œ/g, '"')
        .replace(/â€\u009d/g, '"')
        .replace(/Â/g, '');

    if (!normalized.includes('```')) {
        normalized = normalized.replace(/\s\((\d+)\)\s+/g, '\n$1. ');
    }

    return normalized;
}

export default function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
    if (!content) return null;
    const normalizedContent = normalizeDisplayMarkdown(content);

    return (
        <div className={cn(
            "prose prose-sm dark:prose-invert max-w-none",
            // Headings
            "prose-h1:text-xl prose-h1:font-bold prose-h1:mb-2 prose-h1:mt-4",
            "prose-h2:text-lg prose-h2:font-semibold prose-h2:mb-2 prose-h2:mt-3",
            "prose-h3:text-base prose-h3:font-medium prose-h3:mb-1 prose-h3:mt-2",
            // Paragraphs
            "prose-p:mb-2 prose-p:leading-relaxed",
            // Lists
            "prose-ul:my-2 prose-ol:my-2",
            "prose-li:my-0.5",
            // Code
            "prose-code:text-xs prose-code:bg-slate-100 prose-code:dark:bg-slate-800",
            "prose-code:px-1 prose-code:py-0.5 prose-code:rounded",
            "prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-pre:p-3 prose-pre:rounded-lg",
            // Links
            "prose-a:text-blue-600 prose-a:dark:text-blue-400 prose-a:underline",
            // Blockquotes
            "prose-blockquote:border-l-2 prose-blockquote:border-slate-300",
            "prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-slate-600",
            className
        )}>
            <ReactMarkdown
                components={{
                    // Custom code block handling
                    code({ inline, className, children, ...props }: any) {

                        if (inline) {
                            return (
                                <code
                                    className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded font-mono"
                                    {...props}
                                >
                                    {children}
                                </code>
                            );
                        }

                        return (
                            <pre className="bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto">
                                <code className={cn("text-xs font-mono", className)} {...props}>
                                    {children}
                                </code>
                            </pre>
                        );
                    },
                    // Custom link handling
                    a({ href, children, ...props }) {
                        return (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                {...props}
                            >
                                {children}
                            </a>
                        );
                    }
                }}
            >
                {normalizedContent}
            </ReactMarkdown>
        </div>
    );
}

